import React, { useState } from 'react';
import {
  AppBar,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  Popover,
  Toolbar,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ListIcon from '@material-ui/icons/List';
import SaveIcon from '@material-ui/icons/Save';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { ComputedPlan, Plan } from './types';
import { formatTime, formatMinutes } from './compute';
import Timeline from './Timeline';

const useStyles = makeStyles(theme => ({
  appBar: {
    background: 'linear-gradient(135deg, #7e3db0, #5b2a82)',
    color: '#fff',
  },
  toolbar: {
    minHeight: 72,
    paddingLeft: 8,
    paddingRight: 8,
    gap: 8,
    [theme.breakpoints.up('sm')]: {
      minHeight: 80,
      paddingLeft: 16,
      paddingRight: 16,
    },
  },
  iconBtn: {
    color: '#fff',
  },
  spacer: {
    width: 48,
    flexShrink: 0,
    [theme.breakpoints.up('md')]: {
      width: 96,
    },
  },
  hideOnDesktop: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  center: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    lineHeight: 1.1,
  },
  label: {
    opacity: 0.85,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    fontSize: '0.65em',
  },
  hero: {
    fontSize: 'clamp(1.5rem, 7vw, 2rem)',
    fontWeight: 600,
    lineHeight: 1.05,
  },
  sub: {
    opacity: 0.9,
    fontSize: '0.72em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
  saveStatus: {
    fontSize: '0.6em',
    opacity: 0.8,
    marginTop: 2,
  },
  menuPaper: {
    minWidth: 260,
    maxWidth: 360,
  },
  menuEmpty: {
    padding: '0.75em 1em',
    color: '#666',
    fontSize: '0.9em',
  },
  menuList: {
    padding: 0,
  },
  timelinePaper: {
    minWidth: 280,
    maxWidth: 360,
  },
}));

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface Props {
  computed: ComputedPlan;
  appointmentTime: string;
  planName: string;
  isPersisted: boolean;
  isDirty: boolean;
  saveState: SaveState;
  onSave: () => void;
  plans: Plan[];
  plansLoading: boolean;
  onOpenPlan: (planId: string) => void;
  onCreatePlan: () => void;
  onDeletePlan: (planId: string) => void;
}

export default function LeaveByAppBar({
  computed,
  appointmentTime,
  planName,
  isPersisted,
  isDirty,
  saveState,
  onSave,
  plans,
  plansLoading,
  onOpenPlan,
  onCreatePlan,
  onDeletePlan,
}: Props) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [timelineAnchor, setTimelineAnchor] = useState<null | HTMLElement>(null);

  const hasAppt = !!appointmentTime && !!computed.appointmentAt;

  const saveLabel = saveState === 'saving' ? 'Saving…'
    : saveState === 'saved' ? 'Saved'
    : saveState === 'error' ? 'Save failed'
    : isDirty ? 'Unsaved changes'
    : isPersisted ? 'Saved'
    : 'Not saved';

  const saveDisabled = saveState === 'saving' || (isPersisted && !isDirty);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);
  const openTimeline = (e: React.MouseEvent<HTMLElement>) => setTimelineAnchor(e.currentTarget);
  const closeTimeline = () => setTimelineAnchor(null);

  const handleOpen = (id: string) => { closeMenu(); onOpenPlan(id); };
  const handleNew = () => { closeMenu(); onCreatePlan(); };

  return (
    <AppBar
      position="sticky"
      className={classes.appBar}
      elevation={4}
    >
      <Toolbar className={classes.toolbar}>
        <span className={classes.hideOnDesktop}>
          <Tooltip title="Timeline">
            <IconButton
              className={classes.iconBtn}
              onClick={openTimeline}
              aria-label="show timeline"
            >
              <ScheduleIcon />
            </IconButton>
          </Tooltip>
        </span>
        <div className={classes.spacer} aria-hidden />

        <div className={classes.center}>
          <Typography className={classes.label}>Start At</Typography>
          <Typography className={classes.hero}>
            {hasAppt ? formatTime(computed.leaveByAt) : '--:--'}
          </Typography>
          <Typography className={classes.sub}>
            {hasAppt
              ? `Prep ${formatMinutes(computed.totalPrepMinutes)} · Appt ${formatTime(computed.appointmentAt)}`
              : 'Set an appointment time'}
          </Typography>
          {isPersisted && (
            <Typography className={classes.saveStatus}>
              {planName ? `${planName} · ${saveLabel}` : saveLabel}
            </Typography>
          )}
        </div>

        <Tooltip title={saveDisabled ? saveLabel : 'Save plan'}>
          <span>
            <IconButton
              className={classes.iconBtn}
              onClick={onSave}
              disabled={saveDisabled}
              aria-label="save plan"
            >
              <SaveIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Recent plans">
          <IconButton className={classes.iconBtn} onClick={openMenu} aria-label="recent plans">
            <ListIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        classes={{ paper: classes.menuPaper }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <List dense className={classes.menuList}>
          <ListItem button onClick={handleNew}>
            <AddIcon style={{ marginRight: 8 }} />
            <ListItemText primary="New plan" />
          </ListItem>
          {plansLoading ? (
            <div className={classes.menuEmpty}>Loading…</div>
          ) : plans.length === 0 ? (
            <div className={classes.menuEmpty}>No saved plans yet.</div>
          ) : (
            plans.slice(0, 10).map(plan => {
              const apptDate = plan.appointmentTime ? new Date(plan.appointmentTime) : null;
              const subtitle = apptDate && !isNaN(apptDate.getTime())
                ? apptDate.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                : 'No appointment time';
              return (
                <ListItem button key={plan.id} onClick={() => handleOpen(plan.id)}>
                  <ListItemText primary={plan.name || 'Untitled plan'} secondary={subtitle} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={event => {
                        event.stopPropagation();
                        onDeletePlan(plan.id);
                      }}
                      aria-label="delete plan"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })
          )}
        </List>
      </Menu>

      <Popover
        anchorEl={timelineAnchor}
        open={Boolean(timelineAnchor)}
        onClose={closeTimeline}
        classes={{ paper: classes.timelinePaper }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Timeline computed={computed} hasAppt={hasAppt} />
      </Popover>
    </AppBar>
  );
}
